import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/modern/components/ui/chart";
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import { cn, formatRelativeTime } from "@/modern/utils";
import { useUsersContext } from "../../context/UsersContext";
import type { UserContact } from "../../types";
import { Kpi, ViewAllLink, USERS_PERIOD_TRIGGER_GHOST_CLS } from "../users-ui";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import {
  sortWorkforceAppInstalls,
  type WorkforceAppInstall,
} from "@/other/utils/workforceAppInstalls";
import {
  USERS_CHIP_CLS,
  USERS_EMPTY_CLS,
  USERS_HELPER_CLS,
  USERS_HERO_METRIC_CLS,
  USERS_META_CLS,
  USERS_SUBTITLE_CLS,
  USERS_TITLE_CLS,
} from "../../constants/typography";
import { Popover, PopoverContent, PopoverTrigger } from "@/modern/components/ui/popover";
import { Calendar as CalendarUI } from "@/modern/components/ui/calendar";
import { Calendar, ChevronDown } from "lucide-react";
import type { DateRange } from "react-day-picker";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/modern/components/ui/collapsible";

type DateRangeValue = { from: Date; to: Date };

/** Normalize to start-of-day / end-of-day so ISO date strings stored as UTC midnight fall inside the range. */
function makeRange(from: Date, to: Date): DateRangeValue {
  const f = new Date(from); f.setHours(0, 0, 0, 0);
  const t = new Date(to);   t.setHours(23, 59, 59, 999);
  return { from: f, to: t };
}

/** "7 days" / "2 weeks" / "6 months" / "1 year" / "1.5 years" — returns "All time" for null */
function formatRangeLabel(from: Date | null, to: Date | null): string {
  if (!from || !to) return "All time";
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000);
  if (days <= 1) return "1 day";
  if (days < 14) return `${days} days`;
  if (days < 60) {
    const w = Math.round(days / 7);
    return `${w} ${w === 1 ? "week" : "weeks"}`;
  }
  if (days < 365) {
    const m = Math.round(days / 30.44);
    return `${m} ${m === 1 ? "month" : "months"}`;
  }
  const years = Math.round((days / 365.25) * 2) / 2;
  return `${years} ${years === 1 ? "year" : "years"}`;
}

const POINTS_PRESETS: { label: string; range: () => DateRangeValue | null }[] = [
  { label: "All time", range: () => null },
  { label: "7 days",   range: () => { const t = new Date(), f = new Date(t); f.setDate(f.getDate() - 7);       return makeRange(f, t); } },
  { label: "30 days",  range: () => { const t = new Date(), f = new Date(t); f.setDate(f.getDate() - 30);      return makeRange(f, t); } },
  { label: "3 months", range: () => { const t = new Date(), f = new Date(t); f.setMonth(f.getMonth() - 3);     return makeRange(f, t); } },
  { label: "6 months", range: () => { const t = new Date(), f = new Date(t); f.setMonth(f.getMonth() - 6);     return makeRange(f, t); } },
  { label: "1 year",   range: () => { const t = new Date(), f = new Date(t); f.setFullYear(f.getFullYear()-1); return makeRange(f, t); } },
];

function formatDayLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ─── Section card shell ───────────────────────────────────────────────────────

function SectionCard({
  title,
  hint,
  icon,
  iconTone,
  action,
  children,
  className,
  style,
}: {
  title: string;
  hint?: string;
  icon: ReactNode;
  iconTone: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <section
      style={style}
      className={cn(
        "relative flex flex-col rounded-xl border border-border/60 bg-card overflow-hidden",
        "shadow-[0_1px_0_hsl(var(--foreground)/0.03)]",
        "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1 motion-safe:duration-300 motion-safe:fill-mode-both",
        className,
      )}
    >
      <header className="flex h-12 items-center justify-between gap-3 px-3.5 overflow-hidden border-b border-border/40">
        <div className="flex items-center gap-2 min-w-0">
          <span className={cn("shrink-0 text-base leading-none", iconTone)} aria-hidden>
            {icon}
          </span>
          <p className={cn(USERS_TITLE_CLS, "tracking-tight leading-none truncate")}>
            {title}
          </p>
        </div>
        {(hint || action) ? (
          <div className="flex items-center gap-2 shrink-0 min-w-0">
            {hint ? (
              <p className={cn(USERS_SUBTITLE_CLS, "leading-none truncate max-w-[12rem] text-right")}>
                {hint}
              </p>
            ) : null}
            {action ? <div className="flex items-center gap-1 shrink-0">{action}</div> : null}
          </div>
        ) : null}
      </header>
      <div className="flex-1 px-3.5 pt-2.5 pb-3.5">{children}</div>
    </section>
  );
}

function EmptySlot({ note }: { note: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border/60 bg-background/30 px-3 py-4 text-center">
      <p className={cn(USERS_EMPTY_CLS, "leading-relaxed")}>{note}</p>
    </div>
  );
}

// ─── Contact widget ──────────────────────────────────────────────────────────

function ContactInitials({ name }: { name: string }) {
  const parts = (name ?? "?").trim().split(/\s+/);
  const initials = parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`
    : parts[0].slice(0, 2);
  return (
    <div className={cn("size-8 shrink-0 rounded-full bg-primary/10 text-primary ring-1 ring-inset ring-primary/15 flex items-center justify-center font-semibold uppercase select-none text-sm")}>
      {initials.toUpperCase()}
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      aria-label="Copy"
      className="ml-1 opacity-0 group-hover/channel:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
      onClick={(e) => {
        e.preventDefault();
        navigator.clipboard.writeText(value).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied
        ? <i className="fa-solid fa-circle-check text-success text-sm" />
        : <i className="fa-light fa-copy text-sm" />}
    </button>
  );
}

function ContactRow({ contact }: { contact: UserContact }) {
  const channels: { key: string; icon: ReactNode; iconColor: string; text: string; href?: string }[] = [
    contact.email ? {
      key: "email", iconColor: "text-muted-foreground",
      icon: <i className="fa-light fa-envelope text-sm shrink-0" />,
      text: contact.email, href: `mailto:${contact.email}`,
    } : null,
    contact.phone ? {
      key: "phone", iconColor: "text-muted-foreground",
      icon: <i className="fa-light fa-phone text-sm shrink-0" />,
      text: contact.phone, href: `tel:${contact.phone.replace(/[^\d+]/g, "")}`,
    } : null,
    contact.address ? {
      key: "address", iconColor: "text-muted-foreground",
      icon: <i className="fa-light fa-location-dot text-sm shrink-0" />,
      text: contact.address,
    } : null,
  ].filter(Boolean) as { key: string; icon: ReactNode; iconColor: string; text: string; href?: string }[];

  return (
    <li className="group flex items-start gap-3 py-2.5 border-b border-border/40 last:border-0 transition-colors hover:bg-accent/30 -mx-1 px-1 rounded-lg">
      <ContactInitials name={contact.name ?? "?"} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className={cn(USERS_TITLE_CLS, "truncate leading-none")}>
            {contact.name ?? "—"}
          </p>
          {contact.primary && (
            <span className={cn("inline-flex items-center h-6 px-2 rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/15", USERS_CHIP_CLS)}>
              Primary
            </span>
          )}
          {contact.label && (
            <span className={cn(USERS_SUBTITLE_CLS)}>{contact.label}</span>
          )}
        </div>
        {channels.length > 0 ? (
          <ul className="mt-0.5 space-y-0.5">
            {channels.map((ch) => (
              <li key={ch.key} className="group/channel flex items-center gap-1.5 text-sm leading-snug">
                <span className={ch.iconColor}>{ch.icon}</span>
                {ch.href ? (
                  <a href={ch.href} className="text-foreground/80 hover:text-primary transition-colors truncate">
                    {ch.text}
                  </a>
                ) : (
                  <span className="text-muted-foreground truncate">{ch.text}</span>
                )}
                {ch.href && <CopyButton value={ch.text} />}
              </li>
            ))}
          </ul>
        ) : (
          <p className={cn("mt-1", USERS_HELPER_CLS)}>No channels on file</p>
        )}
      </div>
    </li>
  );
}

// ─── Activity widget ──────────────────────────────────────────────────────────

function installParts(install: WorkforceAppInstall) {
  return {
    version: (install.app_version?.trim?.() || install.app_version || "").trim(),
    model: (install.device_model?.trim?.() || install.device_model || "").trim(),
    seen: install.last_seen_at ? formatRelativeTime(install.last_seen_at) : "",
  };
}

// Samsung tablet model codes start with SM-T / SM-P / SM-X (e.g. SM-X710 = Galaxy Tab S9).
const TABLET_MODEL_RE = /ipad|tablet|^sm-[tpx]\d/i;

function deviceIconClass(model: string) {
  if (!model) return "fa-circle-question";
  return TABLET_MODEL_RE.test(model) ? "fa-tablet-screen-button" : "fa-mobile-screen";
}

function ActivityIcon({ name }: { name: string }) {
  return (
    <span className="flex w-4 shrink-0 justify-center text-muted-foreground/60" aria-hidden>
      <i className={cn("fa-light fa-fw text-sm", name)} />
    </span>
  );
}

const ACTIVITY_ROW_CLS = "flex h-10 items-center gap-3";

function ActivityItem({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: ReactNode;
}) {
  return (
    <div className={ACTIVITY_ROW_CLS}>
      <ActivityIcon name={icon} />
      <span className={cn(USERS_HELPER_CLS, "flex-1 min-w-0 truncate")}>{label}</span>
      <span className={cn(USERS_META_CLS, "text-foreground text-right shrink-0")}>{value}</span>
    </div>
  );
}

function WorkforceAppInstallActivity({
  installs,
  lastInteractionWorkforce,
}: {
  installs: WorkforceAppInstall[] | null | undefined;
  lastInteractionWorkforce: string | Date | null | undefined;
}) {
  const [open, setOpen] = useState(false);
  const sorted = sortWorkforceAppInstalls(installs ?? []);

  if (sorted.length === 0) {
    return (
      <ActivityItem
        icon="fa-download"
        label="Workforce app"
        value={lastInteractionWorkforce ? "Unknown build" : "—"}
      />
    );
  }

  const latest = sorted[0];
  const { version: latestVersion } = installParts(latest);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div>
        <CollapsibleTrigger
          type="button"
          className={cn(
            ACTIVITY_ROW_CLS,
            "group/wf w-full text-left",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm",
          )}
          aria-label={open ? "Hide install history" : "Show install history"}
        >
          <ActivityIcon name="fa-download" />
          <span className={cn(USERS_HELPER_CLS, "flex-1 min-w-0 truncate group-hover/wf:text-foreground/80 transition-colors duration-150")}>
            Workforce app
          </span>
          <span className="inline-flex items-center gap-1.5 shrink-0">
            <span className={cn(USERS_META_CLS, "text-foreground tabular-nums")}>
              {latestVersion || "—"}
            </span>
            <ChevronDown
              className={cn(
                "size-3.5 text-muted-foreground/60 transition-transform duration-200",
                "ease-[cubic-bezier(0.23,1,0.32,1)]",
                "motion-reduce:transition-none",
                open && "rotate-180",
              )}
              strokeWidth={2}
              aria-hidden
            />
          </span>
        </CollapsibleTrigger>

        <CollapsibleContent>
          {/* Bleeds to the SectionCard edges (its body uses px-3.5). */}
          <ul className="-mx-3.5 border-t border-border/40 bg-muted/30 px-3.5 py-1.5">
            {sorted.map((install, index) => {
              const { version, model, seen } = installParts(install);
              return (
                <li
                  key={install.device_id || `${model}-${index}`}
                  className="flex h-8 items-center gap-3 min-w-0"
                >
                  <ActivityIcon name={deviceIconClass(model)} />
                  <span className="flex min-w-0 flex-1 items-baseline gap-1.5 truncate">
                    <span className={cn(USERS_META_CLS, "text-foreground")}>{version || "—"}</span>
                    <span className="truncate text-xs text-muted-foreground">
                      · {model || "Unknown device"}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                    {seen || "—"}
                  </span>
                </li>
              );
            })}
          </ul>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function ActivityPanel({
  lastInteractionHQ,
  lastInteractionWorkforce,
  workforceAppInstalls,
  createdAt,
}: {
  lastInteractionHQ: string | Date | null | undefined;
  lastInteractionWorkforce: string | Date | null | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workforceAppInstalls: any;
  createdAt: string | Date | null | undefined;
}) {
  const memberSince = createdAt
    ? new Date(createdAt).toLocaleDateString(undefined, {
        month: "short",
        year: "numeric",
      })
    : "—";

  return (
    <div className="-mt-2.5 -mb-3.5 divide-y divide-border/40">
      <ActivityItem
        icon="fa-desktop"
        label="Last active in HQ"
        value={formatRelativeTime(lastInteractionHQ)}
      />
      <ActivityItem
        icon="fa-mobile"
        label="Last active in Workforce"
        value={formatRelativeTime(lastInteractionWorkforce)}
      />
      <WorkforceAppInstallActivity
        installs={workforceAppInstalls}
        lastInteractionWorkforce={lastInteractionWorkforce}
      />
      <ActivityItem icon="fa-calendar" label="Member since" value={memberSince} />
    </div>
  );
}

// ─── Role widget ──────────────────────────────────────────────────────────────

function formatCapability(text: string): string {
  const stripped = text.replace(/^this user can\s+/i, "").trim();
  if (!stripped) return text;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

function CapabilityRow({ text, index }: { text: string; index: number }) {
  const label = formatCapability(text);
  return (
    <div
      className="flex items-start gap-2.5 py-2"
      style={{ animationDelay: `${index * 30}ms` }}
      title={text}
    >
      <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center text-success/80" aria-hidden>
        <i className="fa-light fa-check text-sm leading-none" />
      </span>
      <p className={cn(USERS_HELPER_CLS, "text-foreground/80 leading-snug min-w-0")}>
        {label}
      </p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function OverviewTab({ user }: { user: any }) {
  const { setActiveTab, project, showArchived } = useUsersContext();
  const [pointsRange, setPointsRange] = useState<DateRangeValue | null>(null);

  const { version: contactsVersion } = useSwivuStore({ storeId: STORE_KEYS.userContactStore });
  const { version: pointsVersion } = useSwivuStore({ storeId: STORE_KEYS.incentivePointsStore });
  const { version: resourceVersion } = useSwivuStore({ storeId: STORE_KEYS.resourceStore });
  const { version: assignmentVersion } = useSwivuStore({ storeId: STORE_KEYS.assignmentStore });
  const { version: eventVersion } = useSwivuStore({ storeId: STORE_KEYS.eventStore });

  const resolvedContacts = useMemo<UserContact[]>(() => {
    if (!project) {
      if (user.email?.trim()) {
        return [{ id: `fallback-${user.id}`, name: user.name, email: user.email, label: "Self", primary: true }];
      }
      return [];
    }
    const userContacts = project.userContactsStore.query((uc) => uc.userId === user.id);
    const result: UserContact[] = userContacts.map((uc) => {
      const cr = uc.contactRecord as any;
      return {
        id: uc.id,
        name: cr?.name ?? (uc as any).name,
        email: cr?.email ?? (uc as any).email,
        phone: cr?.phone ?? (uc as any).phone,
        address: cr?.address ?? (uc as any).address,
        label: (uc as any).label,
        primary: (uc as any).primary ?? false,
      };
    }).filter((c) => Boolean(c.name || c.email)) as UserContact[];
    if (result.length > 0) return result;
    if (user.email?.trim()) {
      return [{ id: `fallback-${user.id}`, name: user.name, email: user.email, label: "Self", primary: true }];
    }
    return [];
  }, [user.id, user.email, user.name, contactsVersion, project]);

  const { pointsTotal, pointsSeries } = useMemo(() => {
    if (!project) return { pointsTotal: 0, pointsSeries: [] };
    const userPoints = project.incentivePointsStore.query(
      (p: any) => p.boardUserId === user.id && (showArchived || !p.archived),
    );

    // Date range boundaries — -Infinity/Infinity when "All time"
    const fromMs = pointsRange
      ? new Date(pointsRange.from.getFullYear(), pointsRange.from.getMonth(), pointsRange.from.getDate(), 0, 0, 0, 0).getTime()
      : -Infinity;
    const toMs = pointsRange
      ? new Date(pointsRange.to.getFullYear(), pointsRange.to.getMonth(), pointsRange.to.getDate(), 23, 59, 59, 999).getTime()
      : Infinity;

    // Total respects the selected range
    let total = 0;
    for (const p of userPoints) {
      const earnedMs = p.dateEarned ? new Date(p.dateEarned).getTime() : null;
      if (earnedMs != null && earnedMs >= fromMs && earnedMs <= toMs) total += p.points ?? 0;
    }

    // Chart series — derive span from data when "All time"
    let chartFrom: Date;
    let chartTo: Date;
    if (pointsRange) {
      chartFrom = pointsRange.from;
      chartTo   = pointsRange.to;
    } else {
      // Find earliest / latest earned date in data
      let minMs = Infinity;
      let maxMs = -Infinity;
      for (const p of userPoints) {
        const ms = p.dateEarned ? new Date(p.dateEarned).getTime() : null;
        if (ms != null) { if (ms < minMs) minMs = ms; if (ms > maxMs) maxMs = ms; }
      }
      const now = new Date();
      chartTo   = now;
      chartFrom = minMs === Infinity ? now : new Date(minMs);
    }

    const chartFromMs = new Date(chartFrom.getFullYear(), chartFrom.getMonth(), chartFrom.getDate(), 0, 0, 0, 0).getTime();
    const chartToMs   = new Date(chartTo.getFullYear(),   chartTo.getMonth(),   chartTo.getDate(),   23, 59, 59, 999).getTime();
    const spanDays    = Math.max(1, Math.ceil((chartToMs - chartFromMs) / 86_400_000));

    // Bucket by month when span > 365 days (all-time readability)
    const byMonth = spanDays > 365;

    if (byMonth) {
      // Build one bucket per calendar month
      const monthMap = new Map<string, number>();
      for (const p of userPoints) {
        const ms = p.dateEarned ? new Date(p.dateEarned).getTime() : null;
        if (ms == null || ms < chartFromMs || ms > chartToMs) continue;
        const d = new Date(ms);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        monthMap.set(key, (monthMap.get(key) ?? 0) + (p.points ?? 0));
      }
      // Fill every month from chartFrom → chartTo
      const series: { date: string; points: number }[] = [];
      const cur = new Date(chartFrom.getFullYear(), chartFrom.getMonth(), 1);
      const end = new Date(chartTo.getFullYear(), chartTo.getMonth(), 1);
      while (cur <= end) {
        const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`;
        const label = cur.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
        series.push({ date: label, points: monthMap.get(key) ?? 0 });
        cur.setMonth(cur.getMonth() + 1);
      }
      return { pointsTotal: total, pointsSeries: series };
    }

    // Day-by-day buckets
    const buckets = new Array<number>(spanDays).fill(0);
    for (const p of userPoints) {
      const ms = p.dateEarned ? new Date(p.dateEarned).getTime() : null;
      if (ms == null || ms < chartFromMs || ms > chartToMs) continue;
      const idx = Math.min(Math.floor((ms - chartFromMs) / 86_400_000), spanDays - 1);
      if (idx >= 0) buckets[idx] += p.points ?? 0;
    }
    const series = buckets.map((val, i) => {
      const d = new Date(chartFrom);
      d.setDate(chartFrom.getDate() + i);
      return { date: formatDayLabel(d), points: val };
    });

    return { pointsTotal: total, pointsSeries: series };
  }, [user.id, pointsVersion, project, pointsRange, showArchived]);

  const { scheduledHours, resourceCount, resourceIdSet } = useMemo(() => {
    if (!project) return { scheduledHours: 0, resourceCount: 0, resourceIdSet: new Set<string | number>() };
    const userResources = project.resourceStore.query(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (r) => ((r as any).assignedBoardUsers ?? []).includes(user.id),
    );
    const idSet = new Set<string | number>(userResources.map((r) => r.id));

    let totalHours = 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const events = (project as any).eventStore?.records ?? [];
    for (const evt of events) {
      if (!idSet.has(evt.resourceId)) continue;
      if (evt.startDate && evt.endDate) {
        totalHours += (new Date(evt.endDate).getTime() - new Date(evt.startDate).getTime()) / 3_600_000;
      }
    }
    return {
      scheduledHours: Math.round(totalHours),
      resourceCount: userResources.length,
      resourceIdSet: idSet,
    };
  }, [user.id, resourceVersion, eventVersion, project]);

  const taskCount = useMemo(() => {
    if (!project || resourceIdSet.size === 0) return 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const aStore = (project as any).assignmentStore;
    const taskIds = new Set<string | number>();
    if (aStore) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const asgn of aStore.records as any[]) {
        if (!resourceIdSet.has(asgn.resourceId)) continue;
        const evt = asgn.event;
        if (!evt || evt.archived) continue;
        taskIds.add(evt.taskId != null ? evt.taskId : `evt-${evt.id}`);
      }
    }
    return taskIds.size;
  }, [resourceIdSet, assignmentVersion, project]);


  const grants: string[] = (() => {
    try {
      const raw = user.grantsTextOnly;
      if (!Array.isArray(raw)) return [];
      return raw
        .map((g: unknown) => (typeof g === "string" ? g : (g as any)?.description ?? String(g)))
        .filter(Boolean) as string[];
    } catch { return []; }
  })();

  const pointsConfig: ChartConfig = {
    points: { label: "Points", color: "hsl(var(--success))" },
  };

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <Kpi
          label="Scheduled"
          icon={<i className="fa-light fa-clock text-base" />}
          value={`${scheduledHours}h`}
          sub="Total hours scheduled"
        />
        <Kpi
          label="Resources"
          icon={<i className="fa-light fa-screwdriver-wrench text-base" />}
          value={String(resourceCount)}
          sub="Assigned on board"
          tone="neutral"
        />
        <Kpi
          label="Tasks"
          icon={<i className="fa-light fa-swap-arrows text-base" />}
          value={String(taskCount)}
          sub="Total tasks scheduled"
          tone="neutral"
        />
        <Kpi
          label="Points"
          icon={<i className="fa-light fa-award text-base" />}
          value={pointsTotal.toLocaleString()}
          sub="Total points earned"
          tone="neutral"
        />
      </div>

      {/* Two independent columns — no forced row pairing */}
      <div className="grid gap-3 lg:grid-cols-2 items-start">
        {/* Left column */}
        <div className="space-y-3">
          {/* ── Contact ─────────────────────────────────────────────────── */}
          <SectionCard
            title="Contact"
            hint={resolvedContacts.length > 0
              ? `${resolvedContacts.length} linked ${resolvedContacts.length === 1 ? "contact" : "contacts"}`
              : "None linked"}
            icon={<i className="fa-light fa-address-card text-base" />}
            iconTone="text-muted-foreground"
            style={{ animationDelay: "40ms" }}
          >
            {resolvedContacts.length > 0 ? (
              <ul className="-mx-1">
                {resolvedContacts.map((c) => (
                  <ContactRow key={c.id} contact={c} />
                ))}
              </ul>
            ) : (
              <EmptySlot note="No contacts linked to this account yet." />
            )}
          </SectionCard>

          {/* ── Permissions ─────────────────────────────────────────────── */}
          <SectionCard
            title="Permissions"
            hint={grants.length > 0
              ? `${grants.length} permissions`
              : "Access on this board"}
            icon={<i className="fa-light fa-shield-halved text-base" />}
            iconTone="text-muted-foreground"
            style={{ animationDelay: "120ms" }}
          >
            {user.isDeactivated && (
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className={cn("inline-flex items-center rounded-md px-2 py-0.5 bg-destructive/10 text-destructive ring-1 ring-inset ring-destructive/20 leading-none", USERS_CHIP_CLS)}>
                  Deactivated
                </span>
              </div>
            )}
            {grants.length > 0 ? (
              <div>
                <p className={cn(USERS_HELPER_CLS, "mb-1")}>This user can</p>
                <div className="space-y-0.5">
                  {grants.map((g, i) => (
                    <CapabilityRow key={i} text={g} index={i} />
                  ))}
                </div>
              </div>
            ) : (
              <EmptySlot note="Permissions appear when account access is linked." />
            )}
          </SectionCard>
        </div>

        {/* Right column */}
        <div className="space-y-3">
          {/* ── Points ──────────────────────────────────────────────────── */}
          <SectionCard
            title="Points"
            icon={<i className="fa-light fa-award text-base" />}
            iconTone="text-muted-foreground"
            action={
              <>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className={cn(USERS_PERIOD_TRIGGER_GHOST_CLS, "inline-flex items-center w-auto gap-1")}>
                      <Calendar className="size-3.5 shrink-0 opacity-60" />
                      <span className="text-sm font-normal">
                        {formatRangeLabel(pointsRange?.from ?? null, pointsRange?.to ?? null)}
                      </span>
                      <ChevronDown className="size-3 shrink-0 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end" sideOffset={6}>
                    <div className="flex">
                      {/* Preset sidebar */}
                      <div className="flex flex-col gap-0.5 border-r border-border/60 p-2 min-w-[7.5rem]">
                        <p className="px-2 pb-1 text-xs font-medium text-muted-foreground/70 uppercase tracking-wide">
                          Presets
                        </p>
                        {POINTS_PRESETS.map((p) => {
                          const currentLabel = pointsRange
                            ? formatRangeLabel(pointsRange.from, pointsRange.to)
                            : "All time";
                          const active = currentLabel === p.label;
                          return (
                            <button
                              key={p.label}
                              type="button"
                              onClick={() => setPointsRange(p.range())}
                              className={cn(
                                "text-left text-sm px-2.5 py-1.5 rounded-md transition-colors",
                                active
                                  ? "bg-muted text-foreground font-medium"
                                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                              )}
                            >
                              {p.label}
                            </button>
                          );
                        })}
                      </div>
                      {/* Calendar */}
                      <CalendarUI
                        mode="range"
                        selected={pointsRange ? { from: pointsRange.from, to: pointsRange.to } : undefined}
                        onSelect={(range: DateRange | undefined) => {
                          if (range?.from && range?.to)
                            setPointsRange(makeRange(range.from, range.to));
                        }}
                        numberOfMonths={2}
                        initialFocus
                      />
                    </div>
                  </PopoverContent>
                </Popover>
                <ViewAllLink onClick={() => setActiveTab("points")} />
              </>
            }
            style={{ animationDelay: "80ms" }}
          >
            <div className="flex items-baseline gap-2">
              <span className={cn(USERS_HERO_METRIC_CLS, "text-foreground")}>
                {pointsTotal.toLocaleString()}
              </span>
              <span className={USERS_HELPER_CLS}>total earned</span>
            </div>
            <div className="mt-3.5">
              <ChartContainer config={pointsConfig} className="aspect-auto h-[140px] w-full">
                <AreaChart data={pointsSeries} margin={{ left: 4, right: 4, top: 6, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pointsFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--color-points)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="var(--color-points)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeOpacity={0.4} />
                  <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={6} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis hide />
                  <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
                  <Area dataKey="points" type="monotone" stroke="var(--color-points)" strokeWidth={2} fill="url(#pointsFill)" />
                </AreaChart>
              </ChartContainer>
            </div>
          </SectionCard>

          {/* ── Activity ────────────────────────────────────────────────── */}
          <SectionCard
            title="Activity"
            hint="Sessions & presence"
            icon={<i className="fa-light fa-wave-pulse text-base" />}
            iconTone="text-muted-foreground"
            style={{ animationDelay: "160ms" }}
          >
            <ActivityPanel
              lastInteractionHQ={user.lastInteractionHQ}
              lastInteractionWorkforce={user.lastInteractionWorkforce}
              workforceAppInstalls={user.workforceAppInstalls}
              createdAt={user.createdAt}
            />
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
